import React, { useEffect, useState } from "react";
import { proposals } from "@/lib/api";

const METRIC_LABEL = {
  years_experience: "Years of experience",
  staff_count: "Staffing",
  certification: "Certification",
  clearance: "Clearance",
  capability: "Capability",
};

export default function SkillVerification({ proposalId }) {
  const [sv, setSv] = useState(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("all"); // all | met | gap

  useEffect(() => {
    if (!proposalId) return;
    proposals.getSkillVerification(proposalId).then(setSv).catch(() => setSv(null));
  }, [proposalId]);

  const generate = async () => {
    setBusy(true);
    try { setSv(await proposals.generateSkillVerification(proposalId)); setOpen(true); }
    catch (e) { window.alert(e?.response?.data?.detail || "Could not generate verification"); }
    finally { setBusy(false); }
  };

  const decide = (q) => {
    const ua = q.user_answer;
    if (ua && typeof ua === "object" && "meets" in ua) return ua.meets;
    if (typeof ua === "boolean") return ua;
    return !!q.auto_answer?.meets;
  };

  const setAnswer = async (qid, meets) => {
    try {
      const updated = await proposals.answerSkillVerification(proposalId, { [qid]: { meets } });
      setSv(updated);
    } catch (e) { window.alert(e?.response?.data?.detail || "Could not save answer"); }
  };

  const summary = sv?.summary || { total: 0, met: 0, gap: 0, coverage: 0 };
  const cov = Math.round((summary.coverage || 0) * 100);
  const covColor = cov >= 80 ? "var(--success, #0b6623)" : cov >= 50 ? "var(--warning, #b8860b)" : "var(--danger, #9c0006)";
  const questions = (sv?.questions || []).filter((q) => {
    if (filter === "met") return decide(q);
    if (filter === "gap") return !decide(q);
    return true;
  });

  return (
    <div className="card" data-testid="skill-verification">
      <div className="card-header">
        <span className="card-title">RFI Skill Verification</span>
        <div className="flex gap-8 items-center">
          {summary.total > 0 && (
            <span className="pill" style={{ background: covColor, color: "#fff" }}>
              {cov}% coverage · {summary.gap} gap{summary.gap === 1 ? "" : "s"}
            </span>
          )}
          <button className="btn btn-primary btn-sm" onClick={generate} disabled={busy} data-testid="verify-btn">
            {busy ? "Analyzing…" : summary.total ? "↻ Re-run" : "🔎 Verify skills vs. RFI"}
          </button>
          {summary.total > 0 && (
            <button className="btn btn-ghost btn-sm" onClick={() => setOpen(!open)}>{open ? "Hide" : "Show"}</button>
          )}
        </div>
      </div>

      {summary.total === 0 ? (
        <div style={{ padding: "16px 20px", fontSize: 13, color: "var(--text-secondary)" }}>
          Generate Indeed-style verification questions from this RFI's requirements. Answers are
          auto-computed from your objective company data (years, staff, certifications, clearances) so
          you can spot capability gaps before bidding.
        </div>
      ) : open ? (
        <div style={{ padding: "8px 16px 16px" }}>
          <div className="flex gap-8" style={{ margin: "8px 0 12px" }}>
            {["all", "met", "gap"].map((f) => (
              <span key={f} onClick={() => setFilter(f)} className={`pill ${filter === f ? "pill-blue" : "pill-gray"}`} style={{ cursor: "pointer", textTransform: "capitalize" }}>
                {f === "all" ? `All (${summary.total})` : f === "met" ? `Met (${summary.met})` : `Gaps (${summary.gap})`}
              </span>
            ))}
          </div>
          {questions.map((q) => {
            const met = decide(q);
            const ev = q.auto_answer?.evidence || [];
            return (
              <div key={q.id} className="req-row" data-testid={`vq-${q.id}`} style={{ alignItems: "flex-start" }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>
                    {q.requirement_id ? <span style={{ color: "var(--text-muted)" }}>{q.requirement_id} · </span> : null}
                    {q.requirement}
                  </div>
                  <div style={{ fontSize: 11.5, color: "var(--text-secondary)", marginTop: 3 }}>
                    {METRIC_LABEL[q.metric] || q.metric}
                    {q.skill_match ? ` · matched: ${q.skill_match}` : ""}
                    {ev.length ? ` · evidence: ${ev.join(", ")}` : ""}
                  </div>
                </div>
                <div className="flex gap-8 items-center" style={{ marginLeft: 12 }}>
                  <span className={`pill ${met ? "pill-green" : "pill-red"}`}>{met ? "✅ Met" : "❌ Gap"}</span>
                  <button className="btn btn-ghost btn-sm" title="Toggle" onClick={() => setAnswer(q.id, !met)} data-testid={`vq-toggle-${q.id}`}>
                    {met ? "Mark gap" : "Mark met"}
                  </button>
                </div>
              </div>
            );
          })}
          {sv?.generated_at && (
            <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 10 }}>
              Generated {new Date(sv.generated_at).toLocaleString()} · answers you set override the auto-computed result.
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}
